import { useEffect, useState, type ReactNode } from 'react';
import type { VoiceAssignment, VoiceProvider, VoiceSpeaker } from '../../../features/scenarios/api';
import { useToast } from '../../../hooks/useToast';
import { SingleSelectDropdown } from '../../common/SingleSelectDropdown';
import { VoiceSelect, type VoiceSelectVoice } from './VoiceSelect';
import { NARRATION_STYLE_CUSTOM, NARRATION_STYLE_OPTIONS, type NarrationProviderCode } from './constants';
import { useNarrationCapabilities } from './hooks/useNarrationCapabilities';
import { useUpdateScenarioVoiceMap } from './hooks/useScenarioVoiceMap';
import styles from './ScenarioGenerator.module.css';
import own from './NarrationSection.module.css';

const SPEAKER_KIND_LABELS: Record<VoiceSpeaker['kind'], string> = {
  narration: 'ナレーター',
  character: '登場人物',
  actor: 'キャスト',
  label: '名前のみ',
};

const STYLE_DROPDOWN_OPTIONS = NARRATION_STYLE_OPTIONS.flatMap((o) => (
  // 「カスタム入力...」の直前に区切り線 (ナレーター行の読み方と同じ)
  o.value === NARRATION_STYLE_CUSTOM
    ? [{ value: '__divider__', label: '', divider: true }, { value: o.value, label: o.label }]
    : [{ value: o.value, label: o.label }]
));

/**
 * 声の台本 (motion-v2) のナレーションセクションで、話者 1 人の声 (声・読み方) を選ぶ行。
 * プロバイダはセクション共通の「音声AIモデル」(selectedNarrationProvider) を使い、変更したら即 voice_map に保存する
 * (PUT は部分マージ・この話者だけを送る)。カスタムの読み方は入力欄の blur / Enter で保存する。
 * ナレーター行は既存のナレーター行と同じ既定の絞り込み (推奨ナレーター / 地域) を受け取る。
 */
export function SpeakerVoiceRow({
  scenarioId,
  speaker,
  assignment,
  provider,
  voices,
  voicesLoading,
  highlighted,
  disabled,
  defaultGenderFilter,
  defaultAgeFilter,
  defaultAccentCode,
  preferStandardAccent,
  trailing,
  onSaved,
}: {
  scenarioId: string;
  speaker: VoiceSpeaker;
  /** 今の声 (voice_map に無いナレーターは既存のナレーション設定からの既定) */
  assignment: VoiceAssignment | null;
  provider: NarrationProviderCode;
  voices: VoiceSelectVoice[];
  /** ボイス一覧を取得中 (トリガーに「読み込み中...」を出す) */
  voicesLoading?: boolean;
  /** 未割り当てで生成を止めたときの強調 */
  highlighted: boolean;
  disabled?: boolean;
  defaultGenderFilter?: string;
  defaultAgeFilter?: string;
  defaultAccentCode?: string;
  /** 方言の指定が無いシナリオ (声の一覧を「標準語」で絞った状態から始める・表示だけなので声の割り当てに関係なく渡す) */
  preferStandardAccent?: boolean;
  /** 読み方の右に並べる操作 (ナレーター行の「すべてのナレーションを再生」) */
  trailing?: ReactNode;
  onSaved: (speakerId: string, voiceMap: Record<string, VoiceAssignment>) => void;
}) {
  const toast = useToast();
  const updateMutation = useUpdateScenarioVoiceMap(scenarioId);
  const capabilities = useNarrationCapabilities(provider);
  const isNarrator = speaker.kind === 'narration';
  // voice_id はプロバイダ固有なので、選択中のプロバイダと違う割り当ては未選択として見せる
  const value = assignment && assignment.provider === provider ? assignment.voice_id : '';
  const [style, setStyle] = useState<string>(assignment?.style ?? '');
  const [styleText, setStyleText] = useState<string>(assignment?.style_text ?? '');
  // カスタム入力欄へのフォーカスは「カスタム」を選んだ直後だけ。保存済みのカスタムで開いたときに
  // autoFocus が効くと、画面がナレーション欄 (シーン一覧の下) まで勝手にスクロールしてしまう
  const [focusCustomInput, setFocusCustomInput] = useState(false);
  useEffect(() => {
    setStyle(assignment?.style ?? '');
    setStyleText(assignment?.style_text ?? '');
  }, [assignment?.style, assignment?.style_text]);

  const save = async (voiceId: string, nextStyle: string, nextStyleText: string) => {
    if (!voiceId) return;
    const withStyle = capabilities.narration_styles && nextStyle
      && (nextStyle !== NARRATION_STYLE_CUSTOM || nextStyleText.trim());
    const next: VoiceAssignment = {
      provider: provider as VoiceProvider,
      voice_id: voiceId,
      ...(withStyle ? { style: nextStyle, ...(nextStyle === NARRATION_STYLE_CUSTOM ? { style_text: nextStyleText.trim() } : {}) } : {}),
    };
    const unchanged = !!assignment && !speaker.voice_is_default
      && assignment.provider === next.provider
      && assignment.voice_id === next.voice_id
      && (assignment.style ?? null) === (next.style ?? null)
      && (assignment.style_text ?? null) === (next.style_text ?? null);
    if (unchanged) return;
    try {
      const saved = await updateMutation.mutateAsync({ [speaker.speaker_id]: next });
      onSaved(speaker.speaker_id, saved?.voice_map ?? {});
    } catch (error) {
      // 400 VOICE_MAP_INVALID (読み方がプロバイダに無い等) は backend の文言をそのまま出す
      const resp = (error as { response?: { data?: { error?: string } } }).response;
      toast.error(resp?.data?.error || '声の保存に失敗しました');
    }
  };

  const label = isNarrator ? 'ナレーター' : (speaker.label || SPEAKER_KIND_LABELS[speaker.kind]);
  // 絞り込みの既定 (推奨ナレーター) は、声がまだ何も割り当てられていないときだけ渡す (渡すと先頭の声を自動で選んで保存する)。
  // 別プロバイダの声が割り当て済みのときは未選択として見せるだけにし、「音声AIモデル」を見比べただけで声が書き換わらないようにする
  const applyDefaults = !value && !assignment;

  return (
    <div
      className={`${own.field} ${highlighted ? own.speakerRowUnassigned : ''}`}
      data-testid={`narration-speaker-${speaker.speaker_id}`}
      data-unassigned-highlight={highlighted ? 'true' : undefined}
    >
      <span className={styles.bgmInstructionLabel}>
        {label}
        {!isNarrator && <span className={own.speakerKind}>{SPEAKER_KIND_LABELS[speaker.kind]}</span>}
      </span>
      <div className={own.narratorRow}>
        <VoiceSelect
          voices={voices}
          loading={voicesLoading}
          value={value}
          onChange={(voiceId) => void save(voiceId, style, styleText)}
          defaultGenderFilter={applyDefaults ? defaultGenderFilter : undefined}
          defaultAgeFilter={applyDefaults ? defaultAgeFilter : undefined}
          defaultAccentCode={applyDefaults ? defaultAccentCode : undefined}
          preferStandardAccent={preferStandardAccent}
          provider={provider}
        />
        {capabilities.narration_styles && (
          <div onClick={(e) => e.stopPropagation()} style={{ display: 'inline-flex' }}>
            <SingleSelectDropdown
              label="読み方"
              labelOnly
              alwaysShowSelected
              fitContent
              unstyledTrigger
              triggerClassName={own.styleTrigger}
              value={style}
              onChange={(next) => {
                setStyle(next);
                if (next === NARRATION_STYLE_CUSTOM) setFocusCustomInput(true);
                else void save(value, next, styleText);
              }}
              options={STYLE_DROPDOWN_OPTIONS}
              disabled={disabled}
            />
            {style === NARRATION_STYLE_CUSTOM && (
              <input
                type="text"
                className={own.styleCustomInput}
                value={styleText}
                onChange={(e) => setStyleText(e.target.value)}
                onBlur={() => void save(value, style, styleText)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
                    e.preventDefault();
                    void save(value, style, styleText);
                  }
                }}
                placeholder="例: 落ち着いたナレーション"
                maxLength={80}
                disabled={disabled}
                autoFocus={focusCustomInput}
                aria-label={`${label} の読み方のカスタム入力`}
              />
            )}
          </div>
        )}
        {trailing}
      </div>
      {speaker.voice_is_default && value && <span className={own.fieldHint}>前回のナレーションの声</span>}
      {highlighted && <span className={own.speakerUnassignedHint}>声を選んでください</span>}
    </div>
  );
}
